import type { MouseEvent } from 'react';

import { type IconWeight } from '../../icons';
import { availableIcons, availableIconSizes } from '../ButtonIcon';

import { type ActionButtonVariants } from './ActionButton.css';
import * as css from './ActionButton.css';

interface ActionButtonProps {
    onClick?: (event: MouseEvent) => void;

    isDisabled?: boolean;

    /**
     * Visual size of the button.
     *
     * Controlled by the VE variants.
     *
     * @default 'medium'
     */
    buttonSize?: ActionButtonVariants['size'];

    variant?: ActionButtonVariants['variant'];

    /**
     * Icon to render inside the button.
     *
     * Must match a key from `availableIcons`.
     *
     * @default 'plus'
     */
    icon?: keyof typeof availableIcons;

    /**
     * Size of the icon.
     *
     * Falls back to the icon's default size if not provided.
     *
     * @default 'medium'
     */
    iconSize?: keyof typeof availableIconSizes;

    /**
     * Phosphor icon variants
     *
     */
    iconWeight?: IconWeight;

    /**
     * Color of the icon.
     *
     * Accepts any valid CSS color value.
     *
     * @default 'currentColor'
     */
    iconColor?: string;

    /**
     * Native button type attribute.
     *
     * @default 'button'
     */
    type?: 'submit' | 'button';

    'aria-label'?: string;
    remove?: boolean;
}

export function ActionButton({
    icon = 'plus',
    buttonSize = 'medium',
    variant,
    onClick = () => {},
    isDisabled = false,
    iconSize = 'medium',
    iconWeight,
    iconColor = 'currentColor',
    type = 'button',
    'aria-label': ariaLabel,
    remove = false,
}: ActionButtonProps) {
    const Icon = availableIcons[icon].component;
    const size: number = (iconSize && availableIconSizes[iconSize]) || availableIcons[icon].defaultSize;

    return (
        <button
            // css compiler cannot compile type variants correctly
            className={css.actionButton({ size: buttonSize, variant, remove: remove as any })}
            onClick={(e) => onClick(e)}
            disabled={isDisabled}
            type={type}
            aria-label={ariaLabel}
        >
            <Icon width={size} height={size} weight={iconWeight} color={iconColor} />
        </button>
    );
}
